import React, { useState, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, MapPin, Download } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UploadDeclinations() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    processFile(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    processFile(droppedFile);
  };

  const processFile = (selectedFile) => {
    if (selectedFile) {
      if (
        selectedFile.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        selectedFile.type === 'application/vnd.ms-excel'
      ) {
        setFile(selectedFile);
        setUploadResult(null);
        previewFile(selectedFile);
      } else {
        toast.error('Please select a valid Excel file (.xlsx or .xls)');
      }
    }
  };

  const previewFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        
        // Show first 5 rows as preview
        setPreview({
          headers: jsonData.length > 0 ? Object.keys(jsonData[0]) : [],
          rows: jsonData.slice(0, 5),
          totalRows: jsonData.length
        });
      } catch (error) {
        console.error('Error reading file:', error);
        toast.error('Failed to read Excel file');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleUpload = async () => {
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/declinations/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResult({
        success: true,
        total: response.data.total_uploaded,
        geocoded: response.data.geocoded
      });
      toast.success(`Successfully uploaded ${response.data.total_uploaded} declinations`);
    } catch (error) {
      console.error('Upload error:', error);
      const message = error.response?.data?.detail || 'Upload failed';
      setUploadResult({ success: false, error: message });
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create template data
    const template = [
      {
        'Address': '123 Main St',
        'City': 'Houston',
        'State': 'TX',
        'Zip Code': '77001',
        'Discipline': 'PT',
        'Agency': 'Premier Home Care',
        'Date': '2024-01-15',
        'Reason': 'No available therapist'
      }
    ];
    
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Declinations');
    XLSX.writeFile(wb, 'declinations_template.xlsx');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Upload Declinations</h1>
          <p className="text-muted-foreground mt-1">
            Upload an Excel file containing declined visit information
          </p>
        </div>
        <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
          <Download size={16} className="mr-2" />
          Download Template
        </Button>
      </div>

      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-start gap-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <AlertCircle className="text-blue-600 mt-0.5" size={18} />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Required columns:</p>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li><strong>Address</strong> - Street address of the declined visit</li>
                <li><strong>Discipline</strong> - Service type (PT, OT, ST, etc.)</li>
                <li><strong>Date</strong> - Date of declination</li>
              </ul>
              <p className="mt-2 font-medium">Optional columns:</p>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>City, State, Zip Code - For better geocoding accuracy</li>
                <li>Agency - The referring agency/insurance</li>
                <li>Reason - Why the visit was declined</li>
              </ul>
            </div>
          </div>

          <div
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
              dragOver
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-primary/50'
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
            />
            <div className="flex flex-col items-center gap-3">
              {file ? (
                <>
                  <FileSpreadsheet className="w-12 h-12 text-green-600" />
                  <div>
                    <p className="font-medium text-foreground">{file.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-foreground">
                      Drop your Excel file here or click to browse
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Supports .xlsx and .xls files
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {preview && (
            <div className="space-y-2">
              <p className="text-sm font-medium">
                Preview ({preview.totalRows} rows total):
              </p>
              <div className="overflow-x-auto border rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      {preview.headers.map((header, i) => (
                        <th key={i} className="px-3 py-2 text-left font-medium">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, i) => (
                      <tr key={i} className="border-t">
                        {preview.headers.map((header, j) => (
                          <td key={j} className="px-3 py-2 whitespace-nowrap">
                            {row[header]?.toString() || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {uploadResult && (
            <div className={`p-4 rounded-lg ${
              uploadResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
            }`}>
              {uploadResult.success ? (
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 size={20} />
                  <div>
                    <p className="font-medium">Upload successful!</p>
                    <p className="text-sm">
                      {uploadResult.total} declinations uploaded, {uploadResult.geocoded} geocoded
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle size={20} />
                  <p>{uploadResult.error}</p>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-3">
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              className="flex-1"
              data-testid="upload-btn"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Uploading & Geocoding...
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-2" />
                  Upload Declinations
                </>
              )}
            </Button>
            {file && (
              <Button
                variant="outline"
                onClick={() => {
                  setFile(null);
                  setPreview(null);
                  setUploadResult(null);
                }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="text-primary" size={20} />
          <h2 className="text-lg font-semibold">View Declinations Map</h2>
        </div>
        <p className="text-muted-foreground mb-4">
          After uploading, you can view declined visits on a map in the Admin section.
        </p>
        <Button variant="outline" onClick={() => window.location.href = '/admin/declinations-map'}>
          Open Declinations Map
        </Button>
      </Card>
    </div>
  );
}
